/**
 * EvalPhaseStrip — Slice 1b compact phase strip, refined to card layout.
 *
 * Renders each phase as a bordered CARD containing:
 *   1. UPPERCASE letter-spaced mono label (phase name).
 *   2. A thin horizontal PROGRESS BAR whose fill width = phase score % and
 *      whose color follows the score band (green/amber/red via --results-accent-*).
 *   3. Optional numeric score (coach-only, showScore=true).
 *
 * Three cards in a responsive row; stack on mobile (flex-wrap).
 * Click a card → seeks the video to that phase via `onPhaseSeek`.
 *
 * TOKEN CONTRACT (RESULTS_THEME_CONVENTION.md):
 *   Every colour, radius, and font reference goes through a --results-* or
 *   --font-results-* variable. No hex values, no raw font names, no Tailwind
 *   color utilities. This component MUST be rendered inside EvalResultsPageShell
 *   for the tokens to resolve.
 *
 * ROLE-GATING (DESIGN_EVAL_RESULTS_UI.md §2.1, §2.2, owner ruling Q1):
 *   The strip is VISIBLE to ALL users (names + color indicators are meaningful
 *   to athletes). Numeric scores are controlled by `showScore` — coaches may
 *   see a subtle value, athletes do not.
 *
 * SEEK BEHAVIOUR:
 *   Clicking a card calls `onPhaseSeek(phase)`, which maps to the existing
 *   `handlePhaseSeek` in EvaluationResults.tsx — pause + seekToFrame.
 */

import React from 'react';
import type { PhaseScore } from '@/types/api';

// ── Score helpers ─────────────────────────────────────────────────────────────

/** Normalise to 0-100 range (handles both 0-1 and 0-100 input). */
function normalizeScore(score: number | undefined | null): number {
  if (score == null) return 0;
  return score > 1 ? score : score * 100;
}

/** Extract the best available primary score for a phase. */
function getPhaseScore(phase: PhaseScore): number {
  const raw = phase.form_score ?? phase.spatial_score ?? phase.score ?? phase.overall_phase_score;
  return normalizeScore(raw);
}

/**
 * Map a 0-100 score to a --results-accent-* token.
 * Bands: ≥80 = good, 60–79 = warn, <60 = bad.
 */
type AccentToken = '--results-accent-good' | '--results-accent-warn' | '--results-accent-bad';

function scoreBandAccent(score: number): AccentToken {
  if (score >= 80) return '--results-accent-good';
  if (score >= 60) return '--results-accent-warn';
  return '--results-accent-bad';
}

// ── Prop types ────────────────────────────────────────────────────────────────

export interface EvalPhaseStripProps {
  /** Phase score array from enrichedPhaseScores in EvaluationResults. */
  phaseScores: PhaseScore[];
  /**
   * Seek handler passed from EvaluationResults.handlePhaseSeek.
   * Called with the clicked PhaseScore object.
   */
  onPhaseSeek?: (phase: PhaseScore) => void;
  /**
   * When true, show a small numeric score beneath the phase name (coach view).
   * When false (default / athlete view), only the name + progress bar are shown.
   * The bar always reflects the score band so the color signal reaches athletes
   * without exposing the raw number.
   */
  showScore?: boolean;
}

// ── EvalPhaseStrip ────────────────────────────────────────────────────────────

/**
 * Phase strip — bordered card layout.
 *
 * Renders one card per phase. Each card:
 *   1. Phase name label (UPPERCASE, --font-results-mono, letter-spaced)
 *   2. Thin horizontal progress bar (fill% = score, color = score band)
 *   3. Optional score digit (showScore=true for coaches)
 *
 * Cards flow in a row (flex-wrap); on mobile they stack naturally.
 * Keyboard: each card is a <button> with accessible label.
 */
export default function EvalPhaseStrip({
  phaseScores,
  onPhaseSeek,
  showScore = false,
}: EvalPhaseStripProps): React.JSX.Element | null {
  if (!phaseScores || phaseScores.length === 0) return null;

  return (
    <div
      data-testid="eval-phase-strip"
      aria-label="Phase timeline — click a phase to seek the video"
      role="group"
      style={{
        /* Prototype .phase-strip: ONE thin filmstrip row — tight 6px gap, cells
           share the row equally (no card wall). Wraps only when truly cramped. */
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
      }}
    >
      {phaseScores.map((phase, index) => {
        const phaseId = phase.phase_id ?? `phase-${index}`;
        const phaseName = phase.phase_name ?? phase.phase_label ?? `Phase ${index + 1}`;
        const score = getPhaseScore(phase);
        const accentValue = `var(${scoreBandAccent(score)})`;

        return (
          <PhaseCard
            key={phaseId}
            phaseName={phaseName}
            score={score}
            accentValue={accentValue}
            showScore={showScore}
            onActivate={() => onPhaseSeek?.(phase)}
          />
        );
      })}
    </div>
  );
}

// ── PhaseCard ─────────────────────────────────────────────────────────────────

interface PhaseCardProps {
  phaseName: string;
  /** 0-100 score, used for progress fill and optional numeric display. */
  score: number;
  /** Resolved CSS color value from the accent token, e.g. "var(--results-accent-good)". */
  accentValue: string;
  showScore: boolean;
  onActivate: () => void;
}

function PhaseCard({
  phaseName,
  score,
  accentValue,
  showScore,
  onActivate,
}: PhaseCardProps): React.JSX.Element {
  const [hovered, setHovered] = React.useState(false);

  const ariaLabel = showScore
    ? `Seek to ${phaseName} (score: ${Math.round(score)})`
    : `Seek to ${phaseName}`;

  return (
    <button
      type="button"
      data-testid="eval-phase-strip-segment"
      aria-label={ariaLabel}
      onClick={onActivate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        // Reset button defaults
        appearance: 'none',
        cursor: 'pointer',
        textAlign: 'left',

        // Thin filmstrip cell (prototype .phase): faint hairline, quiet dark
        // surface, small radius, tight padding. Hover only warms the border.
        border: `1px solid ${
          hovered
            ? `color-mix(in srgb, ${accentValue} 30%, var(--results-surface-line))`
            : `color-mix(in srgb, var(--results-surface-line) 55%, transparent)`
        }`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '9px',
        background: 'color-mix(in srgb, var(--results-surface-raised-1) 55%, var(--results-surface-base))',
        borderRadius: 'var(--results-radius-sm)',
        padding: '10px 12px 12px',

        transition: 'border-color 160ms ease',

        // Sizing: cells share the row EQUALLY (prototype flex:1) — no max cap.
        flex: '1 1 0',
        minWidth: '110px',
      }}
    >
      {/* Phase name label — UPPERCASE mono, letter-spaced */}
      <span
        data-testid="eval-phase-strip-segment-name"
        style={{
          fontFamily: 'var(--font-results-mono)',
          fontStyle: 'normal',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: hovered ? 'var(--results-text-primary)' : 'var(--results-text-dim)',
          transition: 'color 140ms ease',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          display: 'block',
        }}
      >
        {phaseName}
      </span>

      {/* Progress bar track + fill */}
      <div
        style={{
          width: '100%',
          height: '3px',
          background: 'var(--results-surface-raised-2)',
          borderRadius: 'var(--results-radius-pill)',
          overflow: 'hidden',
        }}
      >
        {/* Fill element — width% = score, color = score band */}
        <span
          data-testid="eval-phase-strip-segment-bar"
          aria-hidden="true"
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.max(0, Math.min(100, score))}%`,
            background: accentValue,
            borderRadius: 'inherit',
            transition: 'width 400ms ease',
          }}
        />
      </div>

      {/* Numeric score — coach-only. Athletes see progress bar colors only. */}
      {showScore && (
        <span
          data-testid="eval-phase-strip-segment-score"
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontStyle: 'normal',
            fontSize: '11px',
            fontWeight: 500,
            color: accentValue,
            letterSpacing: '0.02em',
          }}
        >
          {Math.round(score)}
        </span>
      )}
    </button>
  );
}
